/**
 * Clientseitiger Auswerter fuer Mail_segments.filter_query.
 * Spiegelt resolve-segment v2: gleiche Felder, gleiche Operatoren.
 * Wird in der Kontakt-Liste verwendet, um pro Kontakt alle dynamischen
 * Gruppen-Zugehoerigkeiten zu zeigen, ohne pro Kontakt einen Server-Roundtrip
 * zu machen.
 *
 * Achtung: Diese Auswertung darf bei Datentyp-Edge-Cases (Whitespace, case)
 * minimal von der Server-Seite abweichen. Fuer den UI-Anzeige-Zweck OK.
 */

import type { Contact, Segment } from './supabase';
import type { FilterCondition, FilterGroup } from '../types';

function calcAge(birthDate: string): number {
  const today = new Date();
  const b = new Date(birthDate);
  let age = today.getFullYear() - b.getFullYear();
  const m = today.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
  return age;
}

function evalCondition(c: Contact, cond: FilterCondition): boolean {
  // Virtuelle Haushalts-Felder mappen auf Array-Spalten — gleiche Semantik wie
  // im Edge-Function-Resolver, sonst weicht die UI-Vorschau vom Versand ab.
  if (cond.field === 'household_studio') {
    const arr = c.household_organizations ?? [];
    const needle = String(cond.value ?? '');
    switch (cond.op) {
      case 'eq':
      case 'contains':    return arr.includes(needle);
      case 'neq':         return !arr.includes(needle);
      case 'is_null':     return arr.length === 0;
      case 'is_not_null': return arr.length > 0;
    }
    return false;
  }
  if (cond.field === 'household_member_name') {
    const arr = c.household_member_names ?? [];
    const needle = String(cond.value ?? '').toLowerCase();
    switch (cond.op) {
      case 'eq':          return arr.some(n => n.toLowerCase() === needle);
      case 'neq':         return !arr.some(n => n.toLowerCase() === needle);
      case 'contains':    return arr.some(n => n.toLowerCase().includes(needle));
      case 'is_null':     return arr.length === 0;
      case 'is_not_null': return arr.length > 0;
    }
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v = (c as any)[cond.field];
  switch (cond.op) {
    case 'eq':          return v === cond.value;
    case 'neq':         return v !== cond.value;
    case 'in':          return Array.isArray(cond.value) && (cond.value as unknown[]).includes(v);
    case 'not_in':      return !(Array.isArray(cond.value) && (cond.value as unknown[]).includes(v));
    case 'contains':
      if (cond.field === 'tags') return Array.isArray(c.tags) && c.tags.includes(String(cond.value));
      return typeof v === 'string' && v.toLowerCase().includes(String(cond.value).toLowerCase());
    case 'is_null':     return v === null || v === undefined;
    case 'is_not_null': return v !== null && v !== undefined;
    case 'gt':          return v != null && v >  (cond.value as never);
    case 'gte':         return v != null && v >= (cond.value as never);
    case 'lt':          return v != null && v <  (cond.value as never);
    case 'lte':         return v != null && v <= (cond.value as never);
    case 'between': {
      if (v == null) return false;
      const [lo, hi] = cond.value as [unknown, unknown];
      return v >= (lo as never) && v <= (hi as never);
    }
    case 'age_between': {
      // household_youngest_member_birth_date -> Alter des juengsten aktiven
      // Mitglieds im Haushalt; sonst das Geburtsdatum des Postfach-Inhabers.
      const src = cond.field === 'household_youngest_member_birth_date'
        ? c.household_youngest_member_birth_date
        : c.birth_date;
      if (!src) return false;
      const [minAge, maxAge] = cond.value as [number, number];
      const age = calcAge(src);
      return age >= minAge && age <= maxAge;
    }
  }
  return false;
}

/** Liefert true, wenn der Kontakt zum Filter der Gruppe passt. */
export function contactMatchesSegment(c: Contact, seg: Segment): boolean {
  const filter = seg.filter_query as FilterGroup | null | undefined;
  if (!filter || !filter.conditions || filter.conditions.length === 0) return false;
  const results = filter.conditions.map((cond) => evalCondition(c, cond));
  if (filter.operator === 'OR') return results.some(Boolean);
  return results.every(Boolean);
}
